import React, { useState } from 'react';
import {
  AccessTime,
  AccessTimeOutlined,
  AccountBalance,
  AccountBalanceOutlined,
  AccountCircle,
  AccountCircleOutlined,
  AddAlert,
  AddAlertOutlined,
  Ballot,
  BallotOutlined,
  CalendarToday,
  CalendarTodayOutlined,
  CheckCircle,
  CheckCircleOutline,
  ChevronRight,
  Handshake,
  HandshakeOutlined,
  HowToVote,
  HowToVoteOutlined,
  LocationOn,
  LocationOnOutlined,
  Mail,
  MailOutlined,
  MarkunreadMailbox,
  MarkunreadMailboxOutlined,
  ReplayCircleFilled,
  ReplayOutlined,
  Work,
  WorkOutline,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

import { Link } from 'gatsby';

const toolComponents = {
  absentee: {
    friendlyPath: '/absentee-mail-ballot/',
    cleanPath: '/absentee/',
    icon: <WorkOutline />,
    iconHover: <Work />,
    label: 'Request your absentee or mail ballot',
    stateTool: true,
    priority: 3,
  },
  edr: {
    friendlyPath: '/same-day-registration/',
    cleanPath: '/sdr/',
    icon: <AccessTimeOutlined />,
    iconHover: <AccessTime />,
    label: 'Same day registration',
    stateTool: false,
    priority: 12,
  },
  futurevoter: {
    friendlyPath: '/futurevoter/',
    cleanPath: '/futurevoter',
    icon: <HowToVoteOutlined />,
    iconHover: <HowToVote />,
    label: 'Pre-register to vote (ages 13-17)',
    stateTool: false,
    priority: 5,
  },
  leo: {
    friendlyPath: '/local-election-offices/',
    cleanPath: '/leo/',
    icon: <AccountBalanceOutlined />,
    iconHover: <AccountBalance />,
    label: 'Contact your local election office',
    stateTool: false,
    priority: 11,
  },
  locate: {
    friendlyPath: '/where-to-vote/',
    cleanPath: '/locate/',
    icon: <LocationOnOutlined />,
    iconHover: <LocationOn />,
    label: 'Find out where to vote',
    stateTool: false,
    priority: 8,
  },
  pav: {
    friendlyPath: '/pav/',
    cleanPath: '/pav/',
    icon: <ReplayOutlined />,
    iconHover: <ReplayCircleFilled />,
    label: 'Sign up for permanent absentee voting',
    stateTool: false,
    priority: 7,
  },
  pledge: {
    friendlyPath: '/pledge-to-vote/',
    cleanPath: '/pledge/',
    icon: <HandshakeOutlined />,
    iconHover: <Handshake />,
    label: 'Pledge to vote',
    stateTool: false,
    priority: 13,
  },
  register: {
    friendlyPath: '/register-to-vote/',
    cleanPath: '/register/',
    icon: <AccountCircleOutlined />,
    iconHover: <AccountCircle />,
    label: 'Register to vote',
    stateTool: true,
    priority: 2,
  },
  remind: {
    friendlyPath: '/election-reminders/',
    cleanPath: '/remind/',
    icon: <AddAlertOutlined />,
    iconHover: <AddAlert />,
    label: 'Sign up for election reminders',
    stateTool: false,
    priority: 6,
  },
  trackMail: {
    friendlyPath: '/ballot-tracker/',
    cleanPath: '/ballot-tracker/',
    icon: <MarkunreadMailboxOutlined />,
    iconHover: <MarkunreadMailbox />,
    label: 'Track your absentee or mail ballot',
    stateTool: false,
    priority: 9,
  },
  trackProvisional: {
    friendlyPath: '/provisional-ballot-tracker/',
    cleanPath: '/provisional-ballot-tracker/',
    icon: <BallotOutlined />,
    iconHover: <Ballot />,
    label: 'Track your provisional ballot',
    stateTool: false,
    priority: 10,
  },
  upcoming: {
    friendlyPath: '/upcoming-elections/',
    cleanPath: '/upcoming/',
    icon: <CalendarTodayOutlined />,
    iconHover: <CalendarToday />,
    label: 'Find upcoming elections',
    stateTool: false,
    priority: 4,
  },
  vbm: {
    friendlyPath: '/absentee-mail-ballot/',
    cleanPath: '/votebymail/',
    icon: <MailOutlined />,
    iconHover: <Mail />,
    label: 'Request your mail-in ballot',
    stateTool: true,
    priority: 14,
  },
  verify: {
    friendlyPath: '/am-i-registered-to-vote/',
    cleanPath: '/verify/',
    icon: <CheckCircleOutline />,
    iconHover: <CheckCircle />,
    label: 'Check your registration status',
    stateTool: false,
    priority: 1,
  },
};

export function HeaderButton({ toolData, state, slug, useMicrositePaths }) {
  const [isHovered, setIsHovered] = useState(false);

  const stateText = (stateName, toolId) => {
    if (toolId === 'register') {
      return `Register to vote in ${stateName}`;
    }
    if (toolId === 'vbm' || toolId === 'absentee')
      return `Request your ${stateName} absentee or mail-in ballot`;
    if (toolId === 'trackMail') return `Track your ${stateName} ballot`;
    return toolData.label;
  };

  const path = (stateName, useMicrositePath) => {
    if (useMicrositePath) {
      return `/microsite${toolData.cleanPath}`;
    }
    if (state && toolData.stateTool) {
      return `${toolData.friendlyPath}${slug}`;
    }
    return toolData.friendlyPath;
  };

  return (
    <Link
      key={toolData.label}
      to={path(state, useMicrositePaths)}
      title={state ? stateText(state, toolData.toolId) : toolData.label}
      className='-mb-2 border-0 py-7 decoration-base-content last:mb-0 hover:underline'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <div className='flex justify-between font-bold text-base-content'>
        <div className='flex flex-row'>
          <div className='mr-2 align-middle md:mr-4'>
            {isHovered && toolData.iconHover
              ? toolData.iconHover
              : toolData.icon}
          </div>
          <div className='leading-0 align-middle text-lg'>
            {state ? stateText(state, toolData.toolId) : toolData.label}
          </div>
        </div>
        <div className='hidden md:ml-4 md:block'>
          <ChevronRight />
        </div>
      </div>
    </Link>
  );
}

HeaderButton.propTypes = {
  slug: PropTypes.string,
  state: PropTypes.string,
  useMicrositePaths: PropTypes.bool,
  toolData: PropTypes.shape({
    friendlyPath: PropTypes.string,
    cleanPath: PropTypes.string,
    icon: PropTypes.object,
    iconHover: PropTypes.object,
    toolId: PropTypes.string,
    label: PropTypes.string,
    stateTool: PropTypes.bool,
  }),
};

export default function HeaderButtonMenu({
  tools,
  state,
  slug,
  useMicrositePaths,
}) {
  const toolDataList = [];
  tools.forEach((tool) => {
    toolDataList.push({ ...toolComponents[tool], toolId: tool });
  });
  toolDataList.sort((a, b) => a.priority - b.priority);

  return (
    <div className='card border-[1px] border-base-content bg-base-200 shadow-xl'>
      <div className='card-body divide-y-[1px] divide-base-content px-7 py-0 md:px-7'>
        {toolDataList.map((toolData) => (
          <HeaderButton
            toolData={toolData}
            state={state}
            slug={slug}
            useMicrositePaths={useMicrositePaths}
          />
        ))}
      </div>
    </div>
  );
}

HeaderButtonMenu.propTypes = {
  slug: PropTypes.string,
  state: PropTypes.string,
  tools: PropTypes.arrayOf(PropTypes.string),
  useMicrositePaths: PropTypes.bool,
};

HeaderButtonMenu.defaultProps = {
  useMicrositePaths: false,
};
